import * as React from 'react';

import { AlertMessageContext } from 'root/apps/InventoryCountReconcile/contexts/AlertMessage';
import { parseInventoryCountDTOServer } from 'root/apps/InventoryCountReconcile/data';
import {
  getCommonAlertMessage,
  InventoryCountReconcileError,
} from 'root/apps/InventoryCountReconcile/errors';
import {
  InventoryCountDTO,
  InventoryCountReconcileResponse,
  ReconcileStatus,
} from 'root/apps/InventoryCountReconcile/types';

export interface ReconcileDataReturn {
  isLoading: boolean;
  data?: InventoryCountDTO;
  getReconcileData: () => Promise<void>;
  triggerReconcile: () => Promise<void>;
  closeReconcile: () => Promise<void>;
}

const defaultReconcileDataReturn: ReconcileDataReturn = {
  isLoading: false,
  data: undefined,
  getReconcileData: () => Promise.reject<void>(new Error('getStatusData not initialized')),
  triggerReconcile: () => Promise.reject<void>(new Error('triggerReconcile not initialized')),
  closeReconcile: () => Promise.reject<void>(new Error('closeReconcile not initialized')),
};

export const ReconcileDataContext = React.createContext<ReconcileDataReturn>(
  defaultReconcileDataReturn
);

interface Props {
  data?: InventoryCountDTO;
}

const Provider: React.FC<Props> = ({ data: providerData, children }) => {
  const { setAlertMessage } = React.useContext(AlertMessageContext);

  const [data, setData] = React.useState<InventoryCountDTO | undefined>(providerData);
  const [isLoading, setIsLoading] = React.useState(false);

  const systemCustomerId =
    String(window.merchantos.session.getSystemCustomerID() ?? '') || undefined;
  const filterId = String(window.history.getFilters().id ?? '') || undefined;

  if (!systemCustomerId || !filterId) {
    throw new Error('Invalid systemCustomerId or filterId');
  }

  /**
   * Retrieve the current state and progress of the Reconcile job
   */
  const getReconcileData = async () => {
    setAlertMessage(null);
    setIsLoading(true);
    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${filterId}/Reconcile.json`
      );
      await throwOnResponseError(statusResponse);
      const response: InventoryCountReconcileResponse = await statusResponse.json();
      setData(parseInventoryCountDTOServer(response.InventoryCount));
    } catch (err) {
      if (err instanceof InventoryCountReconcileError) {
        setAlertMessage(err);
      } else {
        setAlertMessage(new InventoryCountReconcileError(getCommonAlertMessage('GENERIC_ERROR')));
      }
    }
    setIsLoading(false);
  };

  /**
   * Start the Reconcile job
   */
  const triggerReconcile = async () => {
    setAlertMessage(null);
    setIsLoading(true);
    // Eagerly update the status to done if the job has already been run.
    // This will prevent the alert message from showing up while we wait for the new job's status.
    eagerUpdateReconcileStatusToDone(data, setData);
    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${filterId}/Reconcile.json`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      );
      await throwOnResponseError(statusResponse);
      const response: InventoryCountReconcileResponse = await statusResponse.json();
      setData(parseInventoryCountDTOServer(response.InventoryCount));
    } catch (err) {
      if (err instanceof InventoryCountReconcileError) {
        setAlertMessage(err);
      } else {
        setAlertMessage(new InventoryCountReconcileError(getCommonAlertMessage('GENERIC_ERROR')));
      }
    }
    setIsLoading(false);
  };

  /**
   * Start the Reconcile job
   */
  const closeReconcile = async () => {
    setAlertMessage(null);
    setIsLoading(true);
    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${filterId}/Reconcile.json?close=true`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      );
      await throwOnResponseError(statusResponse);
      const response: InventoryCountReconcileResponse = await statusResponse.json();
      setData(parseInventoryCountDTOServer(response.InventoryCount));
    } catch (err) {
      if (err instanceof InventoryCountReconcileError) {
        setAlertMessage(err);
      } else {
        setAlertMessage(new InventoryCountReconcileError(getCommonAlertMessage('GENERIC_ERROR')));
      }
    }
    setIsLoading(false);
  };

  /**
   * If the response is not ok, throw an InventoryCountReconcileError
   * Attempts to parse the response body for an errorMessage
   * Default to a generic error message
   */
  const throwOnResponseError: (response: Response) => Promise<void> = async (response) => {
    if (!response.ok) {
      try {
        const errorResponse = await response.json();
        if (errorResponse.InventoryCount.errorMessage) {
          throw new InventoryCountReconcileError(errorResponse.InventoryCount.errorMessage);
        }
        // ensure the try block fails and let the catch block default to a generic error message
        throw new Error();
      } catch (err) {
        if (err instanceof InventoryCountReconcileError) {
          throw err;
        }
        throw new InventoryCountReconcileError(getCommonAlertMessage('GENERIC_ERROR'));
      }
    }
  };

  /**
   * Eagerly transition the reconcileStatus to reconciliation_done when applicable
   */
  const eagerUpdateReconcileStatusToDone = (
    data: InventoryCountDTO | undefined,
    setData: (value: InventoryCountDTO | undefined) => void
  ) => {
    const statuses: ReconcileStatus[] = ['reconciliation_success', 'reconciliation_failed'];
    if (data && statuses.includes(data.reconcileStatus)) {
      setData({ ...data, reconcileStatus: 'reconciliation_done' });
    }
  };

  return (
    <ReconcileDataContext.Provider
      value={{
        isLoading,
        data,
        getReconcileData,
        triggerReconcile,
        closeReconcile,
      }}
    >
      {children}
    </ReconcileDataContext.Provider>
  );
};

export default Provider;
